import type { PricingVersion } from '@app/api/team'
import useIsPricingPlanV2 from '@app/hooks/useIsPricingPlanV2'
import type { ReactNode } from 'react'

interface PricingVersionGateProps {
	teamUuid: string
	renderFor: PricingVersion
	children: ReactNode
	fallback?: ReactNode
	loadingSkeleton?: ReactNode
}

/**
 * Gate component that conditionally renders content based on pricing model of user's team.
 * Ensures pricing model-specific UX without overlap between V1 and V2.
 * Feature flag `growthPricingV2` only affects V2 teams - when disabled, V2 teams see fallback(if provided) or nothing.
 * V1 teams are unaffected by the V2 feature flag and always see V1 content.
 *
 * @param teamUuid - The team UUID to check pricing version for. If not provided, renders null
 * @param renderFor - The pricing version this content should render for (v1 or v2)
 * @param children - Content to render when version matches
 * @param fallback - Optional content to render when version doesn't match or V2 flag is disabled
 * @param loadingSkeleton - Optional content to render during loading state
 *
 * @returns ReactNode | null
 * - Returns null when teamUuid is not provided (can't determine pricing version)
 * - Returns null when hook fails or encounters an error
 * - Returns loadingSkeleton when loading (or null if not provided)
 * - Returns fallback (or null) when V2 team has feature flag disabled
 * - Returns children when pricing version matches renderFor
 * - Returns fallback when pricing version doesn't match
 *
 * @example
 * // Render only for V1 users
 * <PricingVersionGate teamUuid={teamUuid} renderFor="v1">
 *   <OldBanner />
 * </PricingVersionGate>
 *
 * // Render only for V2 users with loading skeleton
 * <PricingVersionGate
 *   teamUuid={teamUuid}
 *   renderFor="v2"
 *   loadingSkeleton={<Skeleton />}
 * >
 *   <NewBanner />
 * </PricingVersionGate>
 */
export const PricingVersionGate = ({
	teamUuid,
	renderFor,
	children,
	fallback,
	loadingSkeleton,
}: PricingVersionGateProps): React.JSX.Element | null => {
	const {
		isPricingPlanV2,
		isLoading,
		isError,
		isEnabled: isGrowthPricingV2FFEnabled,
	} = useIsPricingPlanV2(teamUuid, { skip: !teamUuid })

	if (!teamUuid) {
		return null
	}

	if (isError) {
		return null
	}

	if (isLoading) {
		return loadingSkeleton ? <>{loadingSkeleton}</> : null
	}

	const shouldShowV2Content = isPricingPlanV2 && isGrowthPricingV2FFEnabled
	const shouldShowV1Content = !isPricingPlanV2 // V1 teams always see V1 content regardless of V2 FF flag

	if (renderFor === 'v2' && shouldShowV2Content) {
		return <>{children}</>
	}

	if (renderFor === 'v1' && shouldShowV1Content) {
		return <>{children}</>
	}

	return fallback ? <>{fallback}</> : null
}

export default PricingVersionGate
